import { currentLanguage } from ".";
import { userAccountAjax } from "../ajax/UserAccountAjax";
import { isOnlineNow } from "../offline/connectivity";
import { LanguageCode, isChosenThisSession, isLanguage, rememberLanguage } from "./languages";

const PENDING_PREFIX = "eclipse_language_pending:";
const SAVE_TIMEOUT_MS = 5000;

const pendingKey = (userId: string) => PENDING_PREFIX + userId;

const pending = (userId: string) => {
  try {
    return localStorage.getItem(pendingKey(userId));
  } catch {
    return null;
  }
};

const setPending = (userId: string, code: string) => {
  try {
    localStorage.setItem(pendingKey(userId), code);
  } catch {
    return;
  }
};

const clearPending = (userId: string) => {
  try {
    localStorage.removeItem(pendingKey(userId));
  } catch {
    return;
  }
};

async function saveToAccount(userId: string, code: string) {
  setPending(userId, code);
  if (!isOnlineNow()) return;

  const saved = await userAccountAjax.setOwnLanguage(code, { timeoutMs: SAVE_TIMEOUT_MS })
    .then(response => response.success)
    .catch(() => false);
  if (saved && pending(userId) === code) clearPending(userId);
}

type ChooseOptions = { savingTo?: string | null; beforeReload?: () => Promise<unknown> };

// Note: switching reloads rather than re-rendering in place, because every luxon DateTime keeps the
// locale it was created with, including the ones already parsed into the query cache.
export async function chooseLanguage(code: LanguageCode, { savingTo, beforeReload }: ChooseOptions = {}) {
  rememberLanguage(code);
  if (savingTo) await saveToAccount(savingTo, code).catch(() => undefined);
  await beforeReload?.().catch(() => undefined);
  window.location.reload();
}

export const accountLanguageToAdopt = (language: string | null | undefined): LanguageCode | null =>
  isLanguage(language) && language !== currentLanguage() && !isChosenThisSession() ? language : null;

export async function savePendingLanguage(userId: string | null | undefined) {
  const code = userId ? pending(userId) : null;
  if (userId && code) await saveToAccount(userId, code);
}
